import type { RiskStatsResponse } from "../../api/client";
import { EmptyState, ErrorState, LoadingState } from "@/components/common/states";

interface Props {
  data: RiskStatsResponse | null;
  loading: boolean;
  error: boolean;
}

/**
 * SPEC §14 Analytics — percentiles de la perte pire-cas §7 par cycle réglé.
 * Contrat trois états (B6 read-only). Les lignes « directional / mixed » sont
 * mortes depuis no-def (tout cycle est mono-directionnel) — supprimées.
 */
export default function RiskStats({ data, loading, error }: Props) {
  if (error) {
    return (
      <ErrorState title="Stats de risque indisponibles" testId="rs-error">
        Recharge pour réessayer.
      </ErrorState>
    );
  }
  if (loading || data == null) {
    return <LoadingState lines={3} />;
  }
  if (data.settledCycles === 0) {
    return (
      <EmptyState testId="rs-empty">Aucun cycle réglé pour cette source.</EmptyState>
    );
  }
  const wcl = data.worstCaseLossEur;
  return (
    <div data-testid="rs-data">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm [&>dt]:text-muted-foreground [&>dd]:m-0 [&>dd]:font-mono [&>dd]:tabular-nums [&>dd]:text-foreground">
        <dt>Cycles réglés</dt>
        <dd>{data.settledCycles}</dd>
        <dt>Pire-cas moyen</dt>
        <dd>{fmtEur(wcl.mean)}</dd>
        <dt>Pire-cas p50</dt>
        <dd>{fmtEur(wcl.p50)}</dd>
        <dt>Pire-cas p90</dt>
        <dd>{fmtEur(wcl.p90)}</dd>
        <dt>Pire-cas p99</dt>
        <dd>{fmtEur(wcl.p99)}</dd>
        <dt>Pire-cas max</dt>
        <dd>{fmtEur(wcl.max)}</dd>
      </dl>
    </div>
  );
}

function fmtEur(n: number | null): string {
  if (n == null) return "—";
  return `€${n.toFixed(2)}`;
}
